import type { NuxtApp } from 'nuxt/app';
import { parse } from 'devalue';
import { defineNuxtPlugin } from 'nuxt/app';

const MODULE_PRELOAD_BATCH_SIZE = 8;
const MODULE_PRELOAD_DELAY_MS = 150;

export const FETCH_IDLE_WINDOW_MS = 2000;
export const FETCH_IDLE_MAX_WAIT_MS = 30_000;

declare global {
	interface Window {
		__MONOSPACE_MODULE_PRELOAD__?: string;
	}
}

interface FetchIdleTracker {
	markMounted: () => void;
	restore: () => void;
	waitForIdle: () => Promise<void>;
}

interface ModulePreloadNuxtApp {
	hook: NuxtApp['hook'];
}

export function parseModulePreloadPayload(payload: string): string[] {
	const parsedPayload = parse(payload);

	if (!Array.isArray(parsedPayload)) {
		return [];
	}

	return parsedPayload.filter((href): href is string => typeof href === 'string');
}

function getExistingModulePreloadHrefs(): Set<string> {
	return new Set(
		Array.from(
			document.head.querySelectorAll('link[rel="modulepreload"][href]'),
			(link) => link.getAttribute('href'),
		).filter((href) => href !== null),
	);
}

function appendModulePreloadLink(href: string) {
	const link = document.createElement('link');
	link.rel = 'modulepreload';
	link.as = 'script';
	link.crossOrigin = '';
	link.href = href;
	document.head.append(link);
}

function injectModulePreloadsInBatches(hrefs: string[]) {
	const existingHrefs = getExistingModulePreloadHrefs();
	const pendingHrefs = hrefs.filter((href) => !existingHrefs.has(href));

	if (pendingHrefs.length === 0) {
		return;
	}

	let index = 0;

	function injectNextBatch() {
		const batch = pendingHrefs.slice(index, index + MODULE_PRELOAD_BATCH_SIZE);

		for (const href of batch) {
			appendModulePreloadLink(href);
			existingHrefs.add(href);
		}

		index += MODULE_PRELOAD_BATCH_SIZE;

		if (index < pendingHrefs.length) {
			window.setTimeout(injectNextBatch, MODULE_PRELOAD_DELAY_MS);
		}
	}

	injectNextBatch();
}

export function createFetchIdleTracker(idleMs = FETCH_IDLE_WINDOW_MS, maxWaitMs = FETCH_IDLE_MAX_WAIT_MS): FetchIdleTracker {
	const originalFetch = globalThis.fetch;
	let inFlightCount = 0;
	let isMounted = false;
	let idleTimer: number | null = null;
	let maxWaitTimer: number | null = null;
	let resolveIdleWait: (() => void) | null = null;

	const idleWait = new Promise<void>((resolve) => {
		resolveIdleWait = resolve;
	});

	function clearIdleTimer() {
		if (idleTimer === null) {
			return;
		}

		window.clearTimeout(idleTimer);
		idleTimer = null;
	}

	function clearMaxWaitTimer() {
		if (maxWaitTimer === null) {
			return;
		}

		window.clearTimeout(maxWaitTimer);
		maxWaitTimer = null;
	}

	function resolveWait() {
		clearIdleTimer();
		clearMaxWaitTimer();
		resolveIdleWait?.();
		resolveIdleWait = null;
	}

	function scheduleIdleTimer() {
		if (!isMounted || inFlightCount !== 0 || idleTimer !== null || resolveIdleWait === null) {
			return;
		}

		idleTimer = window.setTimeout(() => {
			resolveWait();
		}, idleMs);
	}

	function scheduleMaxWaitTimer() {
		if (!isMounted || maxWaitTimer !== null || resolveIdleWait === null) {
			return;
		}

		maxWaitTimer = window.setTimeout(() => {
			resolveWait();
		}, maxWaitMs);
	}

	function finishTrackedRequest() {
		inFlightCount = Math.max(0, inFlightCount - 1);
		scheduleIdleTimer();
	}

	globalThis.fetch = ((...args: Parameters<typeof originalFetch>) => {
		clearIdleTimer();
		inFlightCount++;

		try {
			return Promise.resolve(originalFetch(...args)).finally(() => {
				finishTrackedRequest();
			});
		}
		catch (error) {
			finishTrackedRequest();
			throw error;
		}
	}) as typeof originalFetch;

	return {
		markMounted() {
			isMounted = true;
			scheduleMaxWaitTimer();
			scheduleIdleTimer();
		},
		restore() {
			clearIdleTimer();
			clearMaxWaitTimer();
			globalThis.fetch = originalFetch;
		},
		waitForIdle() {
			return idleWait;
		},
	};
}

export function setupModulePreloadRuntime(nuxt: ModulePreloadNuxtApp, payload = window.__MONOSPACE_MODULE_PRELOAD__) {
	if (!payload) {
		return;
	}

	delete window.__MONOSPACE_MODULE_PRELOAD__;
	const hrefs = parseModulePreloadPayload(payload);

	if (hrefs.length === 0) {
		return;
	}

	const fetchIdleTracker = createFetchIdleTracker();

	const startInjecting = () => {
		window.setTimeout(() => {
			injectModulePreloadsInBatches(hrefs);
		}, 0);
	};

	const off = nuxt.hook('app:mounted', () => {
		off();
		fetchIdleTracker.markMounted();

		void fetchIdleTracker.waitForIdle().then(() => {
			fetchIdleTracker.restore();
			startInjecting();
		});
	});
}

export default defineNuxtPlugin({
	name: 'studio-global-modulepreload-runtime',
	enforce: 'pre',
	setup(nuxt) {
		setupModulePreloadRuntime(nuxt);
	},
});
